import React, { useState, useEffect, useMemo } from "react";
import { CONFIG } from "../config";
import axios from "axios";
import {
  downloadPruebasExcel,
  getPruebaRowKey,
} from "../utils/pruebasExport";
import "../css/styles.css";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDuration(seconds) {
  if (seconds == null || seconds === undefined) return "—";
  const total = Number(seconds);
  if (Number.isNaN(total)) return "—";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = Math.floor(total % 60);
  return [hours, minutes, secs].map((value) => String(value).padStart(2, "0")).join(":");
}

export default function Pruebas() {
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [testsCompleted, setTestsCompleted] = useState({});
  const [selectedTest, setSelectedTest] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);

  const fetchResults = async () => {
    try {
      const res = await axios.get(
        `${CONFIG.BACKEND_URL}/backend/getalltests`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const backendResults = res.data;
      setResults(backendResults);

      const counts = backendResults.reduce((acc, item) => {
        acc[item.testName] = (acc[item.testName] || 0) + 1;
        return acc;
      }, {});

      setTestsCompleted(counts);

      if (backendResults.length > 0) {
        setSelectedTest(backendResults[0].testName);
      }
    } catch (error) {
      console.error("Error al cargar pruebas:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  useEffect(() => {
    if (selectedTest) {
      setFilteredResults(results.filter((result) => result.testName === selectedTest));
    } else {
      setFilteredResults(results);
    }
    setSelectedIds(new Set());
  }, [selectedTest, results]);

  const tests = Object.keys(testsCompleted).map((testName) => ({
    name: testName,
    count: testsCompleted[testName],
  }));

  const rowEntries = useMemo(
    () =>
      filteredResults.map((result, index) => ({
        result,
        index,
        key: getPruebaRowKey(result, index),
      })),
    [filteredResults]
  );

  const allSelected =
    rowEntries.length > 0 && rowEntries.every((entry) => selectedIds.has(entry.key));

  const someSelected =
    rowEntries.some((entry) => selectedIds.has(entry.key)) && !allSelected;

  const selectedCount = rowEntries.filter((entry) => selectedIds.has(entry.key)).length;

  const toggleRow = (key) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(rowEntries.map((entry) => entry.key)));
  };

  const handleDownloadAll = () => {
    if (!filteredResults.length) return;
    downloadPruebasExcel({
      testName: selectedTest,
      results: filteredResults,
    });
  };

  const handleDownloadSelected = () => {
    const selectedResults = rowEntries
      .filter((entry) => selectedIds.has(entry.key))
      .map((entry) => entry.result);

    if (!selectedResults.length) return;

    downloadPruebasExcel({
      testName: selectedTest,
      results: selectedResults,
    });
  };

  const columnas = [
    { id: "patientName", name: "Nombre" },
    { id: "gender", name: "Sexo" },
    { id: "age", name: "Edad" },
    { id: "testResultNumber", name: "Puntuación" },
    { id: "testDate", name: "Fecha" },
    { id: "testTime", name: "Tiempo" },
  ];

  const TestCard = ({ title, count, isSelected, onClick }) => (
    <div
      onClick={onClick}
      className={`test-card ${isSelected ? "test-card--selected" : ""}`}
    >
      <h3>{title}</h3>
      <p className="test-card__count">{count} pruebas realizadas</p>
    </div>
  );

  if (loading) {
    return (
      <div className="container fondo-main">
        <div className="p-4">
          <div className="title-text">Cargando pruebas...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container fondo-main">
      <div className="p-4">
        <div className="title-text">Pruebas disponibles</div>
        <div className="test-cards-grid">
          {tests.map((test) => (
            <TestCard
              key={test.name}
              title={test.name}
              count={test.count}
              isSelected={selectedTest === test.name}
              onClick={() => setSelectedTest(test.name)}
            />
          ))}
        </div>

        <div className="pruebas-results-header">
          <div className="title-text mt-4 mb-0">Resultados</div>
          {selectedTest && (
            <div className="pruebas-export-toolbar">
              <button
                type="button"
                className="pruebas-export-btn"
                disabled={filteredResults.length === 0}
                onClick={handleDownloadAll}
              >
                Descargar todas ({filteredResults.length})
              </button>
              <button
                type="button"
                className="pruebas-export-btn pruebas-export-btn--primary"
                disabled={selectedCount === 0}
                onClick={handleDownloadSelected}
              >
                Descargar seleccionadas ({selectedCount})
              </button>
            </div>
          )}
        </div>

        {!selectedTest ? (
          <p className="pruebas-empty">Selecciona una prueba para ver sus resultados.</p>
        ) : filteredResults.length === 0 ? (
          <p className="pruebas-empty">No hay resultados para esta prueba.</p>
        ) : (
          <div className="results-table">
            <div className="results-table__wrapper">
              <table className="table">
                <thead>
                  <tr className="table__header-row">
                    <th className="table__header table__header--check">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={toggleSelectAll}
                        aria-label={`Seleccionar todos los resultados de ${selectedTest}`}
                      />
                    </th>
                    {columnas.map((col) => (
                      <th key={col.id} className="table__header">
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rowEntries.map(({ result, index, key }) => {
                    const isSelected = selectedIds.has(key);
                    return (
                      <tr
                        key={key}
                        className={`table__row ${
                          index % 2 === 0 ? "table__row--even" : "table__row--odd"
                        } ${isSelected ? "table__row--selected" : ""}`}
                      >
                        <td className="table__cell table__cell--check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(key)}
                            aria-label={`Seleccionar resultado de ${result.patientName}`}
                          />
                        </td>
                        <td className="table__cell">{result.patientName}</td>
                        <td className="table__cell">{result.gender}</td>
                        <td className="table__cell">{result.age}</td>
                        <td className="table__cell">{result.testResultNumber}</td>
                        <td className="table__cell">{formatDate(result.testDate)}</td>
                        <td className="table__cell">{formatDuration(result.testTime)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
